import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { cohortService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Plus, X, Loader2, AlertCircle, Users, Search, Trash2, Edit2, FolderOpen, Video,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Create/Edit cohort modal ──────────────────────────────────
const CohortModal = ({ existing, onSave, onClose }) => {
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Cohort name is required'); return; }
    setSaving(true);
    try {
      await onSave({ name, description });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save cohort');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl font-semibold text-forest-900">
            {existing ? 'Edit Cohort' : 'New Cohort'}
          </h3>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700"><X size={18} /></button>
        </div>

        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1.5">Name *</label>
          <input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Students — University of Ghana"
            className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
        </div>
        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1.5">Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
            placeholder="Optional note about who's in this group"
            className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white resize-none" />
        </div>

        <button onClick={handleSave} disabled={saving} className="btn-primary w-full flex items-center justify-center gap-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          {existing ? 'Save Changes' : 'Create Cohort'}
        </button>
      </div>
    </div>
  );
};

// ── Manage members modal — search & add, list & remove ───────
const MembersModal = ({ cohort, onClose, onChanged }) => {
  const { data, loading, error, refetch } = useApi(() => cohortService.getById(cohort.id), []);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const members = data?.cohort?.members ?? data?.members ?? [];

  const runSearch = async (q) => {
    setQuery(q);
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await cohortService.searchAvailable(cohort.id, q.trim());
      const users = res.data?.data?.users ?? res.data?.users ?? [];
      setResults(users);
    } catch {
      // silent — search box just shows no results
    } finally { setSearching(false); }
  };

  const [expertRole, setExpertRole] = useState('CO_EXPERT');

  const handleAdd = async (userId, userRole) => {
    try {
      const assignedRole = userRole === 'EXPERT' ? expertRole : 'STUDENT';
      if (userRole === 'EXPERT') {
        await cohortService.assignInstructor(cohort.id, { userId, role: expertRole });
      } else {
        await cohortService.addMembers(cohort.id, [userId], assignedRole);
      }
      setResults((prev) => prev.filter((u) => u.id !== userId));
      refetch();
      onChanged?.();
      toast.success('Member added to cohort');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not add member');
    }
  };

  const handleRemove = async (userId) => {
    try {
      await cohortService.removeMember(cohort.id, userId);
      refetch();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not remove member');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-start justify-between px-6 py-4 border-b border-cream-100 flex-shrink-0">
          <div>
            <h3 className="font-serif text-lg font-semibold text-forest-900">{cohort.name}</h3>
            <p className="text-xs text-forest-400 mt-0.5">{cohort.description || 'Manage lecturers, teaching assistants, and students'}</p>
          </div>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5">
          {/* Search to add */}
          <div className="bg-cream-50 border border-cream-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-forest-500 uppercase tracking-wider">Add people</h4>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-forest-500">If adding an Expert:</span>
                <select
                  value={expertRole}
                  onChange={(e) => setExpertRole(e.target.value)}
                  className="text-xs bg-white border border-cream-300 rounded-lg px-2 py-1"
                >
                  <option value="PRIMARY_EXPERT">Lecturer</option>
                  <option value="CO_EXPERT">Guest Lecturer</option>
                  <option value="TEACHING_ASSISTANT">Teaching Assistant (TA)</option>
                </select>
              </div>
            </div>

            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest-400" />
              <input value={search}
                onChange={(e) => { setSearch(e.target.value); runSearch(e.target.value); }}
                placeholder="Search clients or experts by name, email, business, city..."
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
            </div>
            {searching && <Loader2 size={14} className="animate-spin text-forest-400" />}
            {results.length > 0 && (
              <div className="space-y-1.5 max-h-52 overflow-y-auto">
                {results.map((u) => {
                  const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
                  const sub = u.role === 'EXPERT' ? 'Expert' : (u.clientProfile?.businessName || u.clientProfile?.city || 'Client');
                  return (
                    <div key={u.id} className="flex items-center gap-3 bg-white rounded-lg px-3 py-2 border border-cream-200">
                      <div className="w-7 h-7 bg-forest-700 rounded-full flex items-center justify-center text-cream-50 text-[10px] font-bold flex-shrink-0">
                        {name[0] || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-forest-800 truncate">{name}</p>
                        <p className="text-[10px] text-forest-400 truncate">{sub} · {u.email}</p>
                      </div>
                      <button onClick={() => handleAdd(u.id, u.role)}
                        className="text-xs font-medium border border-forest-200 text-forest-600 hover:bg-forest-50 px-3 py-1.5 rounded-full transition-colors flex-shrink-0">
                        + Add {u.role === 'EXPERT' ? `as ${expertRole.replace('_', ' ')}` : 'as Student'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {query && !searching && results.length === 0 && (
              <p className="text-xs text-forest-400 italic">No matching people found</p>
            )}
          </div>

          {/* Current members */}
          <div>
            <h4 className="text-xs font-semibold text-forest-500 uppercase tracking-wider mb-2">
              Members {loading ? '' : `(${members.length})`}
            </h4>
            {loading ? (
              <Loader2 size={18} className="animate-spin text-forest-400" />
            ) : error ? (
              <p className="text-sm text-rust-500">Could not load members.</p>
            ) : members.length === 0 ? (
              <p className="text-sm text-forest-400 italic">No one in this cohort yet — search above to add people.</p>
            ) : (
              <div className="divide-y divide-cream-50 border border-cream-200 rounded-xl overflow-hidden">
                {members.map((m) => {
                  const u = m.user || m;
                  const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
                  const roleLabel = m.cohortRole === 'PRIMARY_EXPERT'
                    ? 'Lecturer'
                    : m.cohortRole === 'TEACHING_ASSISTANT'
                      ? 'Teaching Assistant'
                      : m.cohortRole === 'CO_EXPERT' || u.role === 'EXPERT'
                        ? 'Guest Lecturer'
                        : 'Student';

                  return (
                    <div key={u.id} className="flex items-center gap-3 px-4 py-2.5">
                      <div className="w-8 h-8 bg-forest-700 rounded-full flex items-center justify-center text-cream-50 text-xs font-bold flex-shrink-0">
                        {name[0] || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-forest-800 truncate">{name}</p>
                          <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.2 rounded-full ${roleLabel === 'Lecturer'
                            ? 'bg-amber-100 text-amber-800'
                            : roleLabel === 'Teaching Assistant'
                              ? 'bg-blue-100 text-blue-800'
                              : roleLabel === 'Guest Lecturer'
                                ? 'bg-forest-100 text-forest-800'
                                : 'bg-cream-100 text-forest-600'
                            }`}>
                            {roleLabel}
                          </span>
                        </div>
                        <p className="text-[10px] text-forest-400 truncate">{u.email}</p>
                      </div>
                      <button onClick={() => handleRemove(u.id)}
                        className="text-xs text-rust-500 hover:bg-rust-50 px-2.5 py-1.5 rounded-full transition-colors flex-shrink-0">
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────
const AdminCohortsPage = () => {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [managing, setManaging] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const { data, loading, error, refetch } = useApi(
    () => cohortService.getAll({ page: 1, limit: 50 }), [refreshKey]
  );
  const cohorts = Array.isArray(data) ? data : (data?.cohorts ?? []);

  const refresh = () => setRefreshKey((k) => k + 1);

  const handleCreate = async (form) => {
    await cohortService.create(form);
    toast.success('Cohort created!');
    refresh();
  };
  const handleUpdate = async (form) => {
    await cohortService.update(editing.id, form);
    toast.success('Cohort updated');
    refresh();
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this cohort? This does not remove any people from the platform — just the grouping.')) return;
    try {
      await cohortService.remove(id);
      toast.success('Cohort deleted');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete cohort');
    }
  };

  return (
    <DashboardLayout title="Cohorts">
      <div className="max-w-3xl space-y-5">
        <div className="flex items-center justify-between">
          <p className="text-sm text-forest-500">
            Group clients (e.g. by school) so you can target SMS or email blasts at just that group.
          </p>
          <button onClick={() => { setEditing(null); setShowModal(true); }}
            className="btn-primary flex items-center gap-2 text-sm flex-shrink-0">
            <Plus size={14} /> New Cohort
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-4 py-3 text-sm">
            <AlertCircle size={15} /> Could not load cohorts.
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-forest-600" />
          </div>
        ) : cohorts.length === 0 ? (
          <div className="text-center py-16 bg-white border border-cream-200 rounded-2xl text-forest-400">
            <FolderOpen size={36} className="mx-auto mb-3 opacity-30" />
            <p className="font-serif text-lg text-forest-600">No cohorts yet</p>
            <p className="text-sm mt-1 mb-4">Create one to start grouping people — e.g. by school or program.</p>
            <button onClick={() => { setEditing(null); setShowModal(true); }} className="btn-primary inline-flex items-center gap-2">
              <Plus size={14} /> Create Cohort
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {cohorts.map((c) => (
              <div key={c.id} className="bg-white border border-cream-200 rounded-2xl p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="font-serif font-semibold text-forest-900">{c.name}</h4>
                    {c.description && <p className="text-xs text-forest-500 mt-0.5">{c.description}</p>}
                    <p className="text-xs text-forest-400 mt-1.5 flex items-center gap-1">
                      <Users size={11} /> {c._count?.members ?? 0} member{(c._count?.members ?? 0) !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 mt-4 pt-4 border-t border-cream-100 flex-wrap">
                  <Link to={`/admin/cohorts/${c.id}`}
                    className="flex items-center gap-1.5 text-xs font-medium bg-forest-800 text-cream-50 hover:bg-forest-700 px-3.5 py-1.5 rounded-full transition-colors">
                    <Video size={12} /> Open Classroom
                  </Link>
                  <button onClick={() => setManaging(c)}
                    className="flex items-center gap-1.5 text-xs font-medium border border-forest-200 text-forest-600 hover:bg-forest-50 px-3 py-1.5 rounded-full transition-colors">
                    <Users size={12} /> Manage Members
                  </button>
                  <button onClick={() => { setEditing(c); setShowModal(true); }}
                    className="flex items-center gap-1.5 text-xs font-medium border border-cream-200 text-forest-600 hover:border-forest-400 px-3 py-1.5 rounded-full transition-colors">
                    <Edit2 size={12} /> Edit
                  </button>
                  <button onClick={() => handleDelete(c.id)}
                    className="flex items-center gap-1.5 text-xs text-rust-500 border border-rust-200 hover:bg-rust-50 px-3 py-1.5 rounded-full transition-colors font-medium ml-auto">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <CohortModal
          existing={editing}
          onSave={editing ? handleUpdate : handleCreate}
          onClose={() => { setShowModal(false); setEditing(null); }}
        />
      )}

      {managing && (
        <MembersModal cohort={managing} onClose={() => setManaging(null)} onChanged={refetch} />
      )}
    </DashboardLayout>
  );
};

export default AdminCohortsPage;